/**
 * Login screen — entry point for all roles.
 * @viewport both
 */
// src/features/auth/Login.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Link,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from '@/core/contexts/AuthContext';
import { useAuthConfig } from '@/core/contexts/AuthConfigContext';
import { useNotification } from '@/core/contexts/NotificationContext';
import { TOUCH, FONT_SIZE } from '@/core/platform/platformStandards';
import AuthLayout from '@/features/auth/components/AuthLayout';
import authService, { deriveOnboardingRequired } from '@/services/authService';
import { ROUTES } from '@/core/constants/routes';
import { PRODUCT_WALKTHROUGH_URL } from '@/core/constants/externalLinks';

import HFRLogo from '@/components/common/HFRLogo';
import MFAVerification from '@/features/auth/components/MFAVerification';
import RegistrationPendingApproval from '@/features/auth/registration/steps/RegistrationPendingApproval';
import PendingApprovalModal from '@/features/auth/components/PendingApprovalModal';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { 
    login,
    loginWithMagicLink,
    loading, 
    loginLoading, // Add separate login loading state
    error, 
    clearError, 
    isAuthenticated, 
    user,
    mfaRequired
  } = useAuth();
  const { loading: authConfigLoading, enableEmailCodeLogin, enablePasswordLogin } = useAuthConfig();
  const showEmailCode = enableEmailCodeLogin;
  const showPassword = enablePasswordLogin;
  const { notifySuccess, notifyWarning } = useNotification();
  
  // Track reCAPTCHA readiness
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  
  const [formValues, setFormValues] = useState({
    email: '',
    password: ''
  });
  const [authMode, setAuthMode] = useState('magic');
  const [magicSession, setMagicSession] = useState('');
  const [magicCodeSent, setMagicCodeSent] = useState(false);
  const [magicCode, setMagicCode] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [pendingApprovalUser, setPendingApprovalUser] = useState(null);
  const [inactiveUser, setInactiveUser] = useState(false);
  const [showPendingApprovalModal, setShowPendingApprovalModal] = useState(false);

  /** reCAPTCHA v3 tokens are single-use at Google; block overlapping submits (double-click, Enter+click). */
  const magicRequestInFlightRef = useRef(false);
  const magicVerifyInFlightRef = useRef(false);
  const passwordLoginInFlightRef = useRef(false);


  // Add auth-page class to body for reCAPTCHA badge visibility
  useEffect(() => {
    document.body.classList.add('auth-page');
    return () => {
      document.body.classList.remove('auth-page');
    };
  }, []);
  
  // Monitor reCAPTCHA readiness
  useEffect(() => {
    console.log('🔐 [Login] reCAPTCHA Provider Status:', {
      executeRecaptchaExists: !!executeRecaptcha,
      timestamp: new Date().toISOString()
    });
    
    if (executeRecaptcha) {
      console.log('🔐 [Login] ✅ reCAPTCHA is ready!');
      setRecaptchaReady(true);
    } else {
      console.log('🔐 [Login] ⏳ reCAPTCHA not ready yet...');
      setRecaptchaReady(false);
    }
  }, [executeRecaptcha]);

  // Check for timeout or session expiry message
  useEffect(() => {
    // Check for session timeout in location state
    if (location.state?.timeout) {
      notifyWarning('Your session has expired due to inactivity. Please log in again.');
      // Clear location state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate, notifyWarning]);

  useEffect(() => {
    if (authConfigLoading) return;
    if (showEmailCode && !showPassword) {
      setAuthMode('magic');
    } else if (!showEmailCode && showPassword) {
      setAuthMode('password');
    }
  }, [authConfigLoading, showEmailCode, showPassword]);

  // Force navigation if authenticated
  useEffect(() => {
    if (isAuthenticated && user && !loading) {
      const from = location.state?.from;
      const redirect = deriveOnboardingRequired(user)
        ? ROUTES.ONBOARDING
        : (from || ROUTES.DASHBOARD);
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, user, loading, navigate, location.state]);

  const handleAuthModeChange = useCallback((_e, value) => {
    if (!value) return;
    setAuthMode(value);
    setMagicSession('');
    setMagicCodeSent(false);
    setMagicCode('');
    setLoginError('');
    setFormErrors({});
  }, []);

  const handleMagicCodeChange = useCallback((e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setMagicCode(v);
    setLoginError('');
  }, []);

  const handleMagicSendCode = useCallback(async () => {
    const errors = {};
    if (!formValues.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = 'Email is invalid';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!executeRecaptcha) {
      setLoginError('reCAPTCHA is not ready. Please try again.');
      return;
    }
    if (magicRequestInFlightRef.current) {
      return;
    }
    magicRequestInFlightRef.current = true;
    try {
      const recaptchaToken = await Promise.race([
        executeRecaptcha('magic_link_request'),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('reCAPTCHA timeout')), 5000)
        )
      ]);
      if (!recaptchaToken || typeof recaptchaToken !== 'string' || recaptchaToken.length < 10) {
        setLoginError('reCAPTCHA verification failed. Please refresh the page and try again.');
        return;
      }
      const data = await authService.requestMagicLink(formValues.email, recaptchaToken);
      if (data.session) {
        setMagicSession(data.session);
        setMagicCodeSent(true);
        notifySuccess(data.message || 'Check your email for a sign-in code.');
      } else {
        notifySuccess(data.message || 'If this email is registered, you will receive a code shortly.');
      }
    } catch (err) {
      setLoginError(err.message || 'Could not send sign-in code. Please try again.');
    } finally {
      magicRequestInFlightRef.current = false;
    }
  }, [executeRecaptcha, formValues.email, notifySuccess]);

  const handleMagicSignIn = useCallback(async (e) => {
    e.preventDefault();
    if (magicCode.length !== 6) {
      setLoginError('Enter the 6-digit code from your email.');
      return;
    }
    if (!magicSession) {
      setLoginError('Request a new code first.');
      return;
    }
    if (!executeRecaptcha) {
      setLoginError('reCAPTCHA is not ready. Please try again.');
      return;
    }
    if (magicVerifyInFlightRef.current) {
      return;
    }
    magicVerifyInFlightRef.current = true;
    try {
      const recaptchaToken = await Promise.race([
        executeRecaptcha('magic_link_verify'),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('reCAPTCHA timeout')), 5000)
        )
      ]);
      if (!recaptchaToken || typeof recaptchaToken !== 'string' || recaptchaToken.length < 10) {
        setLoginError('reCAPTCHA verification failed. Please refresh the page and try again.');
        return;
      }
      const user = await loginWithMagicLink(
        formValues.email,
        magicSession,
        magicCode,
        recaptchaToken
      );
      notifySuccess(`Welcome, ${user.firstName}!`);
      const isActive = (user.manager_status || user.role_status || '').toLowerCase() === 'active';
      const isPending = (user.approval_status || user.status || '').toLowerCase() === 'pending';
      if (!isActive) {
        setInactiveUser(true);
        return;
      }
      if (isPending && user.pending_admin_review !== true) {
        setPendingApprovalUser(user);
        return;
      }
      setTimeout(() => {
        const redirect = deriveOnboardingRequired(user)
          ? ROUTES.ONBOARDING
          : (location.state?.from || ROUTES.DASHBOARD);
        navigate(redirect, { replace: true });
      }, 100);
    } catch (err) {
      if (err.isPendingApproval || err.type === 'PENDING_APPROVAL') {
        setShowPendingApprovalModal(true);
        return;
      }
      setLoginError(err.message || 'Invalid or expired code. Please try again.');
    } finally {
      magicVerifyInFlightRef.current = false;
    }
  }, [
    executeRecaptcha,
    formValues.email,
    loginWithMagicLink,
    location.state,
    magicCode,
    magicSession,
    navigate,
    notifySuccess
  ]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    setFormErrors(prev => {
      if (prev[name]) {
        return {
          ...prev,
          [name]: ''
        };
      }
      return prev;
    });
    
    // Clear login error when user starts typing
    setLoginError(prev => prev ? '' : prev);
    
    // Clear auth context error when user modifies fields
    if (error) {
      clearError();
    }
  }, [error, clearError]);

  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formValues.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formValues.password) {
      errors.password = 'Password is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formValues.email, formValues.password]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Check if reCAPTCHA is ready
    console.log('🔐 [Login] Step 1 - Checking if executeRecaptcha is available:', !!executeRecaptcha);
    if (!executeRecaptcha) {
      console.error('🔐 [Login] ERROR - executeRecaptcha is not available!');
      setLoginError('reCAPTCHA is not ready. Please try again.');
      return;
    }

    if (passwordLoginInFlightRef.current) {
      return;
    }
    passwordLoginInFlightRef.current = true;

    try {
      console.log('🔐 [Login] Step 2 - Calling executeRecaptcha("login")...');
      // Generate reCAPTCHA token with timeout to prevent hanging
      const recaptchaToken = await Promise.race([
        executeRecaptcha('login'),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('reCAPTCHA timeout')), 5000)
        )
      ]);
      
      console.log('🔐 [Login] Step 3 - Token generated:', {
        exists: !!recaptchaToken,
        type: typeof recaptchaToken,
        isString: typeof recaptchaToken === 'string',
        length: recaptchaToken?.length,
        preview: recaptchaToken ? recaptchaToken.substring(0, 30) + '...' : 'NULL/UNDEFINED'
      });
      
      // Verify token was generated successfully
      if (!recaptchaToken || typeof recaptchaToken !== 'string' || recaptchaToken.length < 10) {
        console.error('🔐 [Login] ERROR - Invalid token:', recaptchaToken);
        setLoginError('reCAPTCHA verification failed. Please refresh the page and try again.');
        return;
      }
      
      console.log('🔐 [Login] Step 4 - Token is valid, calling login API with token...');
      const user = await login(formValues.email, formValues.password, recaptchaToken);

      // MFA required — AuthContext has already set mfaRequired state; just stop here.
      if (user?.mfa_required) {
        return;
      }

      notifySuccess(`Welcome, ${user.firstName}!`);
      
      // Check for approval status and active status
      const isActive = (user.manager_status || user.role_status || '').toLowerCase() === 'active';
      const isPending = (user.approval_status || user.status || '').toLowerCase() === 'pending';

      if (!isActive) {
        setInactiveUser(true);
        return;
      }
      if (isPending && user.pending_admin_review !== true) {
        setPendingApprovalUser(user);
        return;
      }

      // Small delay to ensure reCAPTCHA script completes before navigation
      // This prevents the "Uncaught TypeError" from orphaned reCAPTCHA promises
      setTimeout(() => {
        const redirect = deriveOnboardingRequired(user)
          ? ROUTES.ONBOARDING
          : (location.state?.from || ROUTES.DASHBOARD);
        navigate(redirect, { replace: true });
      }, 100);
    } catch (err) {
      // Check if this is a pending approval error (not a real error, just a status)
      if (err.isPendingApproval || err.type === 'PENDING_APPROVAL') {
        setShowPendingApprovalModal(true);
        return;
      }
      
      // Set the login error to display to the user
      const errorMessage = err.message || 'Invalid email or password. Please try again.';
      setLoginError(errorMessage);
    } finally {
      passwordLoginInFlightRef.current = false;
    }
  }, [formValues.email, formValues.password, executeRecaptcha, login, notifySuccess, location.state, navigate, validateForm]);

  const firmSubmittedPendingReview = location.state?.firmSubmittedPendingReview?.userData;

  if (firmSubmittedPendingReview) {
    return <RegistrationPendingApproval userData={firmSubmittedPendingReview} />;
  }

  if (pendingApprovalUser) {
    return <RegistrationPendingApproval userData={pendingApprovalUser} />;
  }
  if (inactiveUser) {
    return (
      <AuthLayout>
        <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
          <Typography variant="h5" align="center" gutterBottom>
            Access Revoked
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            Your access privileges have been revoked. Please contact support at <a href="mailto:operations@hfr.com">operations@hfr.com</a>.
          </Alert>
        </Box>
      </AuthLayout>
    );
  }

  // Show MFA verification if required
  if (mfaRequired) {
    return (
      <AuthLayout>
        <MFAVerification />
      </AuthLayout>
    );
  }

  if (authConfigLoading) {
    return (
      <AuthLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  if (!showEmailCode && !showPassword) {
    return (
      <AuthLayout>
        <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Sign-in is temporarily unavailable. Please contact{' '}
            <Link href="mailto:operations@hfr.com">operations@hfr.com</Link>.
          </Alert>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
        {/* HFR Connect Logo */}
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            mb: 4
          }}
        >
          <HFRLogo height={120} />
        </Box>

        {loginError && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
          >
            {loginError}
          </Alert>
        )}

        {showEmailCode && showPassword && (
          <Tabs
            value={authMode}
            onChange={handleAuthModeChange}
            variant="fullWidth"
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Email code" value="magic" sx={{ textTransform: 'none', minHeight: TOUCH.minSize }} />
            <Tab label="Password" value="password" sx={{ textTransform: 'none', minHeight: TOUCH.minSize }} />
          </Tabs>
        )}

        {showEmailCode && authMode === 'magic' && !magicCodeSent && (
          <Box component="div">
            <TextField
              name="email"
              label="Email"
              type="email"
              inputMode="email"
              fullWidth
              margin="dense"
              value={formValues.email}
              onChange={handleChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              autoComplete="email"
              inputProps={{ enterKeyHint: 'next' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { minHeight: TOUCH.minSize },
              }}
            />
            <Button
              type="button"
              fullWidth
              variant="contained"
              disabled={loginLoading || !recaptchaReady}
              onClick={handleMagicSendCode}
              sx={{ minHeight: TOUCH.minSize, fontSize: FONT_SIZE.md, mb: 3 }}
            >
              {!recaptchaReady ? 'Loading security check...' : loginLoading ? <CircularProgress size={TOUCH.iconSize} /> : 'Send sign-in code'}
            </Button>
          </Box>
        )}

        {showEmailCode && authMode === 'magic' && magicCodeSent && (
          <form onSubmit={handleMagicSignIn}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: FONT_SIZE.md }}>
              Enter the 6-digit code sent to {formValues.email}
            </Typography>
            <TextField
              label="Sign-in code"
              type="text"
              inputMode="numeric"
              fullWidth
              margin="dense"
              value={magicCode}
              onChange={handleMagicCodeChange}
              autoComplete="one-time-code"
              inputProps={{ maxLength: 6, enterKeyHint: 'go' }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { minHeight: TOUCH.minSize },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loginLoading || !recaptchaReady || magicCode.length !== 6}
              sx={{ minHeight: TOUCH.minSize, fontSize: FONT_SIZE.md, mb: 2 }}
            >
              {loginLoading ? <CircularProgress size={TOUCH.iconSize} /> : 'Sign in'}
            </Button>
            <Button
              type="button"
              fullWidth
              variant="text"
              onClick={() => {
                setMagicSession('');
                setMagicCodeSent(false);
                setMagicCode('');
                setLoginError('');
              }}
              sx={{ minHeight: TOUCH.minSize, mb: 3 }}
            >
              Use a different email
            </Button>
          </form>
        )}

        {showPassword && authMode === 'password' && (
        <form onSubmit={handleSubmit}>
          <TextField
            name="email"
            label="Email"
            type="email"
            inputMode="email"
            fullWidth
            margin="dense"
            value={formValues.email}
            onChange={handleChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
            autoComplete="email"
            inputProps={{ enterKeyHint: 'next' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { minHeight: TOUCH.minSize },
            }}
          />
          
          <TextField
            name="password"
            label="Password"
            type="password"
            fullWidth
            margin="dense"
            value={formValues.password}
            onChange={handleChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
            autoComplete="current-password"
            inputProps={{ enterKeyHint: 'go' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': { minHeight: TOUCH.minSize },
            }}
          />
          
          {/* Remember me and Forgot password on same row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1.5, sm: 0 },
              mt: 2,
              mb: 3,
            }}
          >
            <FormControlLabel
              control={<Checkbox size="small" />}
              label={
                <Typography variant="body2" sx={{ fontSize: FONT_SIZE.md, color: 'text.secondary' }}>
                  Remember me
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Link
              component={RouterLink}
              to="/forgot-password"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: FONT_SIZE.md,
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loginLoading || !recaptchaReady}
            sx={{ minHeight: TOUCH.minSize, fontSize: FONT_SIZE.md, mb: 3 }}
          >
            {!recaptchaReady ? 'Loading security check...' : loginLoading ? <CircularProgress size={TOUCH.iconSize} /> : 'Log in'}
          </Button>
        </form>
        )}

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: FONT_SIZE.md }}>
            Or{' '}
            <Link
              component={RouterLink}
              to="/register"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Register now!
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: FONT_SIZE.md, mt: 1 }}>
            Questions about signing up?{' '}
            <Link
              component={RouterLink}
              to="/help/faq"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              View FAQ
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: FONT_SIZE.md, mt: 1 }}>
            Explore first?{' '}
            <Link
              href={PRODUCT_WALKTHROUGH_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Demo
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Pending Approval Modal */}
      <PendingApprovalModal
        open={showPendingApprovalModal}
        onClose={() => setShowPendingApprovalModal(false)}
      />
    </AuthLayout>
  );
};

export default Login;
